import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

type Language = 'en' | 'hi' | 'ta' | 'te' | 'bn';
type Translations = Record<string, string>;

interface LanguageContextType {
    language: Language;
    setLanguage: (language: Language) => void;
    t: (key: string) => string;
    languageLabels: Record<Language, string>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const languageLabels: Record<Language, string> = {
    en: 'English',
    hi: 'हिन्दी',
    ta: 'தமிழ்',
    te: 'తెలుగు',
    bn: 'বাংলা'
};


export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [language, setLanguage] = useState<Language>('en');
    const [translations, setTranslations] = useState<Translations>({});

    useEffect(() => {
        let isCancelled = false; // Flag to prevent race conditions

        const loadTranslations = async () => {
            try {
                // Always load English as base/fallback
                let baseTranslations = {};
                if (language !== 'en') {
                    try {
                        const enResponse = await fetch(`./locales/en.json`);
                        if (enResponse.ok) {
                            baseTranslations = await enResponse.json();
                        }
                    } catch (e) {
                        console.warn("Failed to load base English translations");
                    }
                }

                // Load target language
                const response = await fetch(`./locales/${language}.json`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch translations: ${response.statusText}`);
                }
                const data = await response.json();
                
                if (!isCancelled) {
                    // Merge: Target language overrides English base
                    setTranslations({ ...baseTranslations, ...data });
                }
            } catch (error) {
                console.error(`Could not load translations for ${language}. Falling back to English.`, error);
                if (isCancelled) return; 

                // If loading target failed, try loading English specifically if we haven't already
                if (language !== 'en') {
                    try {
                        const fallbackResponse = await fetch(`./locales/en.json`);
                        if (fallbackResponse.ok) {
                            const fallbackData = await fallbackResponse.json();
                            if (!isCancelled) {
                                setTranslations(fallbackData);
                            }
                        }
                    } catch (fallbackError) {
                        console.error(`Could not load fallback English translations.`, fallbackError);
                        if (!isCancelled) setTranslations({});
                    }
                } else {
                     if (!isCancelled) setTranslations({});
                }
            }
        };

        loadTranslations();

        return () => {
            isCancelled = true;
        };
    }, [language]);

    const t = useMemo(() => (key: string): string => {
        return translations[key] || key;
    }, [translations]);

    const value: LanguageContextType = {
        language,
        setLanguage,
        t,
        languageLabels,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};